import type {AutoDeliveryDcxUpdateReasonStatus} from '@/consts/icc/iccAutoDeliveryStatusTypes';
import {
    AUTO_DELIVERY_DCX_UPDATE_REASON_STATUS_TYPES,
    isAutoDeliveryDcxOrderExpected,
    validateAutoDeliveryDcxOrderStatus,
} from '@/consts/icc/iccAutoDeliveryStatusTypes';
import appDateTimeService from '@/services/appDateTimeService';
import appContent from '@/services/content/appContent.typed';
import type {InlineNotificationModel} from '@/types/aem';
import {
    AUTO_DELIVERY_INFO_BOX_SOURCES,
    type AutoDeliveryInfoBoxData,
    type AutoDeliveryInfoBoxSource,
} from '@/types/content';
import type {AutoDeliveryDetailsDcx} from '@/types/icc/autoDeliveryDetails';
import stringUtils from '@/utils/stringUtils';

const AUTO_DELIVERY_DCX_ACTIVE_VIEW_TYPES = {
    EXPECTED: 'EXPECTED',
    ACTIVE: 'ACTIVE',
} as const;

type AutoDeliveryDcxActiveViewType =
    (typeof AUTO_DELIVERY_DCX_ACTIVE_VIEW_TYPES)[keyof typeof AUTO_DELIVERY_DCX_ACTIVE_VIEW_TYPES];

const STATUS_UPDATE_REASON_TO_ACTIVE_VIEW: Partial<Record<string, AutoDeliveryDcxActiveViewType>> = {
    [AUTO_DELIVERY_DCX_UPDATE_REASON_STATUS_TYPES.ERR_AD_STOCK_01]: AUTO_DELIVERY_DCX_ACTIVE_VIEW_TYPES.EXPECTED,
    [AUTO_DELIVERY_DCX_UPDATE_REASON_STATUS_TYPES.ERR_AD_LSP_01]: AUTO_DELIVERY_DCX_ACTIVE_VIEW_TYPES.ACTIVE,
    [AUTO_DELIVERY_DCX_UPDATE_REASON_STATUS_TYPES.ERR_AD_LSP_02]: AUTO_DELIVERY_DCX_ACTIVE_VIEW_TYPES.ACTIVE,
    [AUTO_DELIVERY_DCX_UPDATE_REASON_STATUS_TYPES.AD_ERROR_01]: AUTO_DELIVERY_DCX_ACTIVE_VIEW_TYPES.ACTIVE,
    [AUTO_DELIVERY_DCX_UPDATE_REASON_STATUS_TYPES.AD_ERROR_02]: AUTO_DELIVERY_DCX_ACTIVE_VIEW_TYPES.ACTIVE,
    [AUTO_DELIVERY_DCX_UPDATE_REASON_STATUS_TYPES.ERR_AD_STOCK_02]: AUTO_DELIVERY_DCX_ACTIVE_VIEW_TYPES.ACTIVE,
    [AUTO_DELIVERY_DCX_UPDATE_REASON_STATUS_TYPES.AD_HOLD_02]: AUTO_DELIVERY_DCX_ACTIVE_VIEW_TYPES.ACTIVE,
    [AUTO_DELIVERY_DCX_UPDATE_REASON_STATUS_TYPES.AD_HOLD_05]: AUTO_DELIVERY_DCX_ACTIVE_VIEW_TYPES.ACTIVE,
    [AUTO_DELIVERY_DCX_UPDATE_REASON_STATUS_TYPES.AD_HOLD_06]: AUTO_DELIVERY_DCX_ACTIVE_VIEW_TYPES.ACTIVE,
};

/**
 * Determines whether to show the Expected view (true) or Active view (false).
 * StatusUpdateReason takes priority over orderStatus.
 * Unknown statusUpdateReason → Expected view.
 */
export const isAutoDeliveryDcxActiveExpectedView = (
    orderStatus?: string | null,
    statusUpdateReason?: string | null
): boolean => {
    if (statusUpdateReason) {
        const mappedView = STATUS_UPDATE_REASON_TO_ACTIVE_VIEW[statusUpdateReason];

        // Unknown reason → show Expected view
        if (mappedView === undefined) {
            return true;
        }

        return mappedView === AUTO_DELIVERY_DCX_ACTIVE_VIEW_TYPES.EXPECTED;
    }

    // No statusUpdateReason: unknown orderStatus → Expected view (unknownState notification)
    if (!validateAutoDeliveryDcxOrderStatus(orderStatus)) {
        return true;
    }

    return isAutoDeliveryDcxOrderExpected(orderStatus);
};

const ACTIVE_CONTENT_VARIANTS = {
    OUT_OF_STOCK_ON_HOLD: 'outOfStockOnHold',
    OUT_OF_STOCK_PARTIAL_DELIVERY: 'outOfStockPartialDelivery',
    SKIPPED_DELIVERY: 'skippedDelivery',
    EXPECTED_DELIVERY: 'expectedDelivery',
    ACTIVE_DELIVERY: 'activeDelivery',
    UNKNOWN_STATE: 'unknownState',
} as const;

type ActiveContentVariant = (typeof ACTIVE_CONTENT_VARIANTS)[keyof typeof ACTIVE_CONTENT_VARIANTS];

const OUT_OF_STOCK_ON_HOLD_REASONS = new Set<string>([
    AUTO_DELIVERY_DCX_UPDATE_REASON_STATUS_TYPES.ERR_AD_LSP_01,
    AUTO_DELIVERY_DCX_UPDATE_REASON_STATUS_TYPES.ERR_AD_LSP_02,
    AUTO_DELIVERY_DCX_UPDATE_REASON_STATUS_TYPES.AD_ERROR_01,
    AUTO_DELIVERY_DCX_UPDATE_REASON_STATUS_TYPES.AD_ERROR_02,
]);

export const isActiveOutOfStockOnHold = (statusUpdateReason: AutoDeliveryDcxUpdateReasonStatus | null): boolean => {
    if (!statusUpdateReason) return false;
    return OUT_OF_STOCK_ON_HOLD_REASONS.has(statusUpdateReason);
};

const isUnknownActiveState = (orderStatus?: string | null, statusUpdateReason?: string | null): boolean => {
    if (statusUpdateReason) {
        return STATUS_UPDATE_REASON_TO_ACTIVE_VIEW[statusUpdateReason] === undefined;
    }
    return !validateAutoDeliveryDcxOrderStatus(orderStatus);
};

interface GetActiveContentVariantParams {
    statusUpdateReason: AutoDeliveryDcxUpdateReasonStatus | null;
    isExpectedView: boolean;
    isDeliverySkipped?: boolean;
    isUnknownState: boolean;
}

const getActiveContentVariant = (params: GetActiveContentVariantParams): ActiveContentVariant | null => {
    const {statusUpdateReason, isExpectedView, isDeliverySkipped, isUnknownState} = params;

    if (isExpectedView) {
        if (isUnknownState) return ACTIVE_CONTENT_VARIANTS.UNKNOWN_STATE;
        switch (statusUpdateReason) {
            case AUTO_DELIVERY_DCX_UPDATE_REASON_STATUS_TYPES.ERR_AD_STOCK_01:
                return ACTIVE_CONTENT_VARIANTS.OUT_OF_STOCK_ON_HOLD;
            default:
                if (isDeliverySkipped) return ACTIVE_CONTENT_VARIANTS.SKIPPED_DELIVERY;
                return ACTIVE_CONTENT_VARIANTS.EXPECTED_DELIVERY;
        }
    }

    if (isActiveOutOfStockOnHold(statusUpdateReason)) {
        return ACTIVE_CONTENT_VARIANTS.OUT_OF_STOCK_ON_HOLD;
    }

    if (statusUpdateReason === AUTO_DELIVERY_DCX_UPDATE_REASON_STATUS_TYPES.ERR_AD_STOCK_02) {
        return ACTIVE_CONTENT_VARIANTS.OUT_OF_STOCK_PARTIAL_DELIVERY;
    }

    return ACTIVE_CONTENT_VARIANTS.ACTIVE_DELIVERY;
};

const formatNotificationDescription = (
    notification: InlineNotificationModel | undefined,
    args: string[]
): InlineNotificationModel | undefined => {
    if (!notification?.description || !args.length) return notification;

    return {...notification, description: stringUtils.formatString(notification.description, ...args)};
};

interface GetActiveInfoBoxDataParams {
    details: AutoDeliveryDetailsDcx | undefined;
    source: AutoDeliveryInfoBoxSource;
    isDeliverySkipped?: boolean;
}

export const getActiveInfoBoxData = (params: GetActiveInfoBoxDataParams): AutoDeliveryInfoBoxData | undefined => {
    const {details, source, isDeliverySkipped} = params;
    const statusUpdateReason = details?.autoDelivery?.statusUpdateReason ?? null;
    const orderStatus = details?.order?.status;

    const isExpectedView = isAutoDeliveryDcxActiveExpectedView(orderStatus, statusUpdateReason);
    const isUnknownState = isUnknownActiveState(orderStatus, statusUpdateReason);
    const isFreezedOrder = details?.autoDelivery?.isFreezed;
    const hasFreezeDate = !!details?.autoDelivery?.freezeDate;

    const variant = getActiveContentVariant({statusUpdateReason, isExpectedView, isDeliverySkipped, isUnknownState});

    if (!variant) return undefined;

    const content = appContent.getAutoDeliveryContent();
    const isDashboard = source === AUTO_DELIVERY_INFO_BOX_SOURCES.DASHBOARD;

    const getOutOfStockArgs = (): string[] => {
        const nextDeliveryDate = details?.autoDelivery?.nextDeliveryDate;
        const deliveryMonth = nextDeliveryDate ? appDateTimeService.getLocalizedMonth(nextDeliveryDate) : undefined;

        return deliveryMonth ? [deliveryMonth] : [];
    };

    const getExpectedDeliveryArgs = (): string[] => {
        const freezeDate = details?.autoDelivery?.freezeDate;
        const freezeDateFormatted = freezeDate ? appDateTimeService.getLocalizedDayMonthAndYear(freezeDate) : undefined;

        return freezeDateFormatted ? [freezeDateFormatted] : [];
    };

    const buildResult = (notification: InlineNotificationModel | undefined): AutoDeliveryInfoBoxData | undefined => {
        if (!notification) return undefined;

        return {notification};
    };

    switch (variant) {
        case ACTIVE_CONTENT_VARIANTS.OUT_OF_STOCK_ON_HOLD:
            return buildResult(
                formatNotificationDescription(
                    isExpectedView
                        ? isDashboard
                            ? content?.dashboardContent?.activeExpectedOrderOutOfStockOnHoldDashboard
                                  ?.inlineNotification
                            : content?.extendedDetailsContent?.activeExpectedOrderOutOfStockOnHold
                                  ?.orderDetailsInlineNotification
                        : isDashboard
                          ? content?.dashboardContent?.activeOrderOutOfStockOnHoldDashboard?.inlineNotification
                          : content?.extendedDetailsContent?.activeOrderOutOfStockOnHold
                                ?.orderDetailsInlineNotification,
                    getOutOfStockArgs()
                )
            );
        case ACTIVE_CONTENT_VARIANTS.OUT_OF_STOCK_PARTIAL_DELIVERY:
            return buildResult(
                formatNotificationDescription(
                    isDashboard
                        ? content?.dashboardContent?.activeOrderOutOfStockPartialDeliveryDashboard?.inlineNotification
                        : content?.extendedDetailsContent?.activeOrderOutOfStockPartialDelivery
                              ?.orderDetailsInlineNotification,
                    getOutOfStockArgs()
                )
            );
        case ACTIVE_CONTENT_VARIANTS.SKIPPED_DELIVERY:
            return buildResult(content?.skipDeliveryContent?.nextAutoDeliverySkippedInlineNotification);
        case ACTIVE_CONTENT_VARIANTS.EXPECTED_DELIVERY:
            return isDashboard || !hasFreezeDate
                ? buildResult(undefined)
                : buildResult(
                      formatNotificationDescription(
                          content?.extendedDetailsContent?.activeOrderExpected?.orderDetailsInlineNotification,
                          getExpectedDeliveryArgs()
                      )
                  );
        case ACTIVE_CONTENT_VARIANTS.ACTIVE_DELIVERY:
            return isDashboard || !isFreezedOrder
                ? buildResult(undefined)
                : buildResult(content?.extendedDetailsContent?.activeOrder?.orderDetailsInlineNotification);
        case ACTIVE_CONTENT_VARIANTS.UNKNOWN_STATE:
            return buildResult(
                isDashboard
                    ? content?.dashboardContent?.unknownStateDashboard?.inlineNotification
                    : content?.extendedDetailsContent?.unknownState?.orderDetailsInlineNotification
            );
        default:
            return undefined;
    }
};
